import {useCallback, useEffect, useState} from 'react';
import useSavedComponentsState from '../../../../hooks/use-saved-components-state';
import useIsMounted from '../../../../hooks/use-is-mounted';
import BroadcastService from '../../../../../core/services/broadcast';
import CacheService from '../../../../../core/services/cache/cache_service';
import EnvService from '../../../../../core/services/env';
import type {AppBarSearchMode, AppBarSearchModeSavedState} from './types';

export const APP_BAR_SEARCH_MODE_STORAGE_NAME = 'app_bar_search_mode';
export const APP_BAR_SEARCH_MODE_STORAGE_VERSION = '1.0.0';
export const DEFAULT_APP_BAR_SEARCH_MODE: AppBarSearchMode = 'nexus';

const isValidMode = (value: unknown): value is AppBarSearchMode =>
    value === 'nexus' || value === 'system';

/**
 * Resolves the initial mode from local CacheService for a synchronous first paint.
 */
const getInitialMode = (): AppBarSearchMode => {
    const cached = CacheService.getAppBarSearchMode();
    return isValidMode(cached) ? cached : DEFAULT_APP_BAR_SEARCH_MODE;
};

/**
 * Operator-persisted app-bar search mode (Nexus vs System).
 * Hydrates immediately from CacheService, then reconciles with the API saved-components state.
 */
const useAppBarSearchMode = () => {
    const isMounted = useIsMounted();
    const showToggle = EnvService.DEV_showPanelSearchCommand;

    const {highestVersion: getCachedMode, save: saveCachedMode} = useSavedComponentsState<AppBarSearchModeSavedState>({
        name: APP_BAR_SEARCH_MODE_STORAGE_NAME,
        version: APP_BAR_SEARCH_MODE_STORAGE_VERSION,
    });

    const [savedMode, setSavedMode] = useState<AppBarSearchMode>(getInitialMode);
    const [loading, setLoading] = useState(showToggle);

    /**
     * Loads the operator preference from the API and mirrors it into CacheService.
     */
    useEffect(() => {
        if (!showToggle) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        void (async () => {
            const cached = await getCachedMode();
            if (cancelled || !isMounted()) {
                return;
            }
            const value = cached?.data?.value;
            if (isValidMode(value)) {
                setSavedMode(value);
                CacheService.setAppBarSearchMode(value);
                BroadcastService.postMessage(BroadcastService.channelNames.appBarSearchMode, value);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [getCachedMode, isMounted, showToggle]);

    /**
     * Syncs search mode across tabs / mounts via broadcast.
     */
    useEffect(() => {
        if (!showToggle) {
            return;
        }

        const {close} = BroadcastService.addEventListener(
            BroadcastService.channelNames.appBarSearchMode,
            'message',
            (mode: AppBarSearchMode) => {
                if (isValidMode(mode)) {
                    setSavedMode(mode);
                    CacheService.setAppBarSearchMode(mode);
                }
            },
        );

        return () => {
            close();
        };
    }, [showToggle]);

    /**
     * Persists the operator preference locally (immediate) and via saved-components API.
     */
    const setMode = useCallback(async (next: AppBarSearchMode) => {
        if (!isValidMode(next) || !showToggle) {
            return;
        }

        setSavedMode(next);
        CacheService.setAppBarSearchMode(next);
        BroadcastService.postMessage(BroadcastService.channelNames.appBarSearchMode, next);
        await saveCachedMode({value: next});
    }, [saveCachedMode, showToggle]);

    return {
        showToggle,
        loading,
        savedMode,
        /** Same as `savedMode`; prefer saved selection on every route (including Nexus page). */
        effectiveMode: savedMode,
        setMode,
    };
};

export default useAppBarSearchMode;
